import { useState } from "react";
import { Link } from "react-router-dom";
import NetworkGraph from "../../components/NetworkGraph";
import Reveal from "../../components/Reveal";
import { IconShield } from "../../components/Icons";

const ACCENT = "#7B3FF2";

const FAQS = [
  {
    question: "Por que não basta um antivírus comum?",
    answer:
      "Antivírus tradicionais usam assinaturas de vírus conhecidos. O Bitdefender GravityZone com EDR detecta comportamentos suspeitos — incluindo ameaças novas e ataques de ransomware que os antivírus comuns não conhecem. É a diferença entre reativo e proativo.",
  },
  {
    question: "O que é EDR na prática?",
    answer:
      "EDR (Endpoint Detection and Response) monitora continuamente o comportamento dos processos nos seus computadores. Quando detecta algo suspeito — mesmo sem assinatura de vírus — ele isola, alerta e responde automaticamente, antes que o dano se espalhe.",
  },
  {
    question: "A sepolTECH gerencia tudo ou preciso de alguém de TI?",
    answer:
      "A sepolTECH gerencia toda a plataforma: instalação, configuração, monitoramento de alertas, atualizações e resposta a incidentes. Você não precisa ter equipe de TI interna para isso.",
  },
];

const FEATURES = [
  {
    title: "EPP — Endpoint Protection",
    desc: "Antivírus corporativo, controle de aplicações, firewall gerenciado e proteção web",
  },
  {
    title: "EDR — Detecção e Resposta",
    desc: "Detecção de comportamentos suspeitos, resposta a incidentes e visibilidade total do endpoint",
  },
  {
    title: "Anti-Ransomware",
    desc: "Proteção específica contra ataques de ransomware, com rollback automático de arquivos",
  },
  {
    title: "Gestão Centralizada",
    desc: "Console GravityZone gerenciado pela sepolTECH — você não precisa se preocupar com nada",
  },
  {
    title: "Relatórios de Segurança",
    desc: "Relatórios periódicos sobre ameaças detectadas, bloqueadas e status de proteção",
  },
  {
    title: "Resposta a Incidentes",
    desc: "Em caso de ameaça ativa, a sepolTECH age em conjunto com o EDR para conter e remediar",
  },
];

export default function SepolSecurity() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <Link to="/produtos/produtos">Produtos</Link>
            <span>/</span>
            <span>sepolSECURITY</span>
          </div>
          <div className="tag animate-up animate-up--d1" style={{ color: ACCENT, background: "rgba(123,63,242,.12)", borderColor: `${ACCENT}40` }}>
            <IconShield style={{ width: "13px", height: "13px" }} />
            EPP + EDR · Bitdefender GravityZone
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(40px,6vw,72px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.05",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            sepol<span style={{ color: ACCENT }}>SECURITY</span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "540px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Segurança corporativa com Bitdefender GravityZone gerenciado
            pela sepolTECH. EPP + EDR integrado — proteção avançada com
            detecção e resposta a ameaças em tempo real.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Contratar sepolSECURITY →
            </Link>
            <Link to="/contato" className="btn btn-outline">
              Falar com especialista
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ RECURSOS INCLUSOS ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="tag" style={{ color: ACCENT, background: "rgba(123,63,242,.12)", borderColor: `${ACCENT}40` }}>
              Recursos inclusos
            </div>
            <h2 className="section-title" style={{ marginBottom: "36px" }}>
              O que você recebe com <span style={{ color: ACCENT }}>sepolSECURITY</span>
            </h2>
            <div className="feature-list">
              {FEATURES.map((feature) => (
                <div className="feature-item" key={feature.title}>
                  <div
                    className="feature-item__icon"
                    style={{ background: "rgba(123,63,242,.12)", color: ACCENT }}
                  >
                    ✓
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>{feature.title}</strong> —{" "}
                    {feature.desc}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="section section--alt">
        <div className="container" style={{ maxWidth: "760px" }}>
          <Reveal className="text-center" style={{ marginBottom: "40px" }}>
            <div className="tag" style={{ color: ACCENT, background: "rgba(123,63,242,.12)", borderColor: `${ACCENT}40` }}>
              FAQ
            </div>
            <h2 className="section-title">
              Dúvidas sobre <span style={{ color: ACCENT }}>sepolSECURITY</span>
            </h2>
          </Reveal>

          {FAQS.map((faq, index) => (
            <Reveal
              className={`faq-item${openFaq === index ? " open" : ""}`}
              delay={index * 0.05}
              key={faq.question}
            >
              <button
                className="faq-btn"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className="faq-btn__icon">+</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">
              Proteção corporativa real para sua empresa
            </h2>
            <p className="cta-banner__sub">
              Bitdefender GravityZone gerenciado pela sepolTECH — EPP + EDR
              sem complexidade.
            </p>
            <div className="cta-banner__actions">
              <Link to="/contato" className="btn btn-primary">
                Contratar sepolSECURITY →
              </Link>
              <Link to="/produtos/produtos" className="btn btn-outline">
                Ver outros produtos
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
